import { message } from "antd"

import { useSetDialerSnooze } from "~/hooks/people-os"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"

import {
  entryPersonId,
  getDialerCallHistory,
  getDialerCallSummary,
  type IDialerQueueEntry,
  completeDialerSession
} from "~/api/dialer"
import { setCallHold } from "~/api/voice"
import { useVoiceDevice } from "~/hooks/use-voice-device"
import useGlobalStore from "~/store/global"
import useVoiceSessionStore, {
  completeCallingHours
} from "~/store/voice-session"
import { ITokenInfo } from "~/types/global"
import type { IPhoneNumber } from "~/types/people-os"
import { orderedDialNumbers, type DialNumber } from "./dial-numbers"

// The session engine behind the in-row dialer (Final V2, screen 5).
//
// Two parts on purpose:
//
//   DialerRunProvider  state only — queue, position, stage, paused. Mounted in
//                      root.tsx above the route Outlet, so a run survives row
//                      advances, tab switches AND navigating anywhere else in
//                      OrbitShift. Only End Session and Exit Dialer end it.
//   DialerEngine       owns the Twilio device and places the calls. Mounted
//                      ONLY while a run is live.
//
// The split exists because useVoiceDevice builds and registers a Device the
// moment it is called. With it in the provider, every visit to the People page
// fetched a voice token, constructed a Device and enumerated audio hardware
// before the rep had asked to call anybody, and the page took visibly longer
// to load. Mounting the engine only during a run restores what happened when
// the dialer was a modal: no session, no device.
//
// Not a copy of BulkDialer4L's engine either — it drives the same primitives
// (ensureSession, enqueueQueue, reserveCall, markQueueEntryStatus, makeCall,
// logCall) with much smaller state.

export type RunPerson = {
  id: string
  name: string
  title?: string
  phone?: string
  phone_numbers?: IPhoneNumber[]
  tz?: string
  linkedin_url?: string
  location_country?: string
  account?: { id: string; name: string }
  listId?: string
}

type Stage = "idle" | "connecting" | "ringing" | "live" | "wrap"

// Statuses that mean the leg is over. The callee hanging up from their handset
// reaches us this way — Twilio's status webhook, not a browser event — which
// is why the panel used to sit on "ringing" after the other side had gone.
const TERMINAL_CALL_STATUSES = [
  "COMPLETED",
  "NO_ANSWER",
  "BUSY",
  "FAILED",
  "CANCELED",
  "OUTSIDE_HOURS"
]

// Why a call ended. Only an unanswered call rolls on by itself: a conversation
// that happened — or one the rep hung up deliberately — keeps them on the
// screen so they can log it, and "Log & dial next" is what moves on.
type EndReason = "answered" | "no_answer" | "manual" | "skipped" | null

// Beat before an unanswered call rolls to the next number, so the outcome is
// readable and Pause is reachable.

// What the engine hands back so context actions can reach the device without
// the provider ever holding it.
type EngineHandle = {
  hangup: () => void
  endCall: () => Promise<void>
  hangupCall: () => void
  toggleMute: () => void
  sendDigits: (d: string) => void
  setHold: (hold: boolean) => Promise<boolean>
  markSkipped: () => Promise<void>
  log: (v: {
    disposition?: string
    notes?: string
    personId?: string
  }) => Promise<boolean>
}

type DialerRunValue = {
  running: boolean
  paused: boolean
  stage: Stage
  /** How the last call ended. Nothing advances on its own, so the rep reads
   *  this on the wrap screen to see whether it rang, was never dialled, or
   *  they hung up. */
  endReason: EndReason
  index: number
  queue: RunPerson[]
  current: RunPerson | null
  next: RunPerson | null
  elapsed: number
  completed: number
  muted: boolean
  held: boolean
  deviceStatus: string
  /** AI summary of the call that just ended, once the backend has it. */
  aiSummary: string | null
  /** True when the backend gave up generating one. */
  summaryFailed: boolean
  start: (people: RunPerson[]) => void
  syncQueue: (people: RunPerson[]) => void
  end: () => void
  togglePause: () => void
  toggleMute: () => void
  sendDigits: (d: string) => void
  toggleHold: () => void | Promise<void>
  snooze: () => void
  disconnect: () => void
  /** Call the SAME person again after the call has ended. */
  redial: () => void
  logAndAdvance: (
    v: { disposition?: string; notes?: string; personId?: string },
    advance: boolean
  ) => Promise<void>
  /** A run that advances on its own counts down first, and the rep can stop
   *  it. Null while nothing is pending. */
  /** The callee's line is actually ringing, as Twilio reported it. The stage
   *  turns "ringing" the moment we start dialling, which is earlier. */
  calleeRinging: boolean
  advanceCountdown: number | null
  cancelAdvance: () => void
  advanceNow: () => void
  /** Snoozed while their call is still up: move on once it ends, not now. */
  snoozeCurrentAfterCall: () => void
  dialNumbers: DialNumber[]
  dialNumberIndex: number
  activeDialNumber: DialNumber | null
}

const AUTO_ADVANCE_SECONDS = 5

const DialerRunContext = createContext<DialerRunValue | null>(null)

export function useDialerRun() {
  const ctx = useContext(DialerRunContext)
  if (!ctx) {
    throw new Error("useDialerRun must be used inside DialerRunProvider")
  }
  return ctx
}

export default function DialerRunProvider({
  children
}: {
  children: React.ReactNode
}) {
  const engineRef = useRef<EngineHandle | null>(null)
  const liveSessionRef = useRef<number | null>(null)
  const outerToken = useGlobalStore((store) => store.tokenInfo)

  const [running, setRunning] = useState(false)
  const [paused, setPaused] = useState(false)
  const [stage, setStage] = useState<Stage>("idle")
  const [dialNumberIndex, setDialNumberIndex] = useState(0)
  const [queue, setQueue] = useState<RunPerson[]>([])
  const [index, setIndex] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [logged, setLogged] = useState<Set<string>>(new Set())
  const [held, setHeld] = useState(false)
  const { mutateAsync: setSnooze } = useSetDialerSnooze()
  const [muted, setMuted] = useState(false)
  const [deviceStatus, setDeviceStatus] = useState("idle")
  const [aiSummary, setAiSummary] = useState<string | null>(null)
  const [endReason, setEndReason] = useState<EndReason>(null)
  const [summaryFailed, setSummaryFailed] = useState(false)

  const current = queue[index] ?? null
  const next = queue[index + 1] ?? null
  const dialNumbers = useMemo(
    () => (current ? orderedDialNumbers(current) : []),
    [current]
  )
  const activeDialNumber = dialNumbers[dialNumberIndex] ?? null

  // The live timer. Only runs while a call is up, so a wrap-up screen never
  // keeps counting.
  useEffect(() => {
    if (stage !== "live") return
    setElapsed(0)
    const t = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [stage])

  const start = useCallback((people: RunPerson[]) => {
    if (!people.length) {
      message.error("Nobody to call")
      return
    }
    setQueue(people)
    setIndex(0)
    setLogged(new Set())
    setElapsed(0)
    setPaused(false)
    setHeld(false)
    setStage("connecting")
    setRunning(true)
    setAiSummary(null)
    setEndReason(null)
    setSummaryFailed(false)
  }, [])

  // Keeps a running queue in step with who is callable now.
  //
  // The queue used to be a snapshot taken at Start Session, so anyone who
  // became callable while the rep was dialling - a calling window widened, a
  // snooze lifted, a number enriched - sat outside the run, and the progress
  // total disagreed with the Callable tab beside it.
  //
  // Everyone up to and including the person on the phone is left alone: they
  // have been dialled, or are being dialled, and a list that reshuffles under
  // a live call is worse than one that is a little behind. Only the tail is
  // replaced.
  const syncQueue = useCallback(
    (people: RunPerson[]) => {
      setQueue((prev) => {
        if (!prev.length) return prev
        const dialled = prev.slice(0, index + 1)
        const seen = new Set(dialled.map((p) => String(p.id)))
        const next = [
          ...dialled,
          ...people.filter((p) => !seen.has(String(p.id)))
        ]
        // Same people in the same order means no state change, so the effect
        // driving this cannot loop.
        if (
          next.length === prev.length &&
          next.every((p, i) => String(p.id) === String(prev[i].id))
        ) {
          return prev
        }
        return next
      })
    },
    [index]
  )

  // The ONLY way a session ends: End Session and Exit Dialer both land here.
  //
  // Started BEFORE the state reset, because setRunning(false) unmounts the
  // engine and nulls the handle. The promise it returns keeps running after
  // the engine has gone — it closes over what it needs — so the server calls
  // still land even though nothing is left on screen to wait for them.
  const end = useCallback(() => {
    if (engineRef.current) {
      void engineRef.current.endCall()
    } else if (outerToken && liveSessionRef.current) {
      void completeDialerSession(outerToken, liveSessionRef.current).catch(
        () => undefined
      )
    }
    liveSessionRef.current = null
    setRunning(false)
    setPaused(false)
    setStage("idle")
    setQueue([])
    setIndex(0)
    setElapsed(0)
    setHeld(false)
  }, [outerToken])

  const snoozedDuringCallRef = useRef(false)
  const autoAdvance = useVoiceSessionStore((s) => s.autoAdvance)
  const [calleeRinging, setCalleeRinging] = useState(false)
  const [advanceCountdown, setAdvanceCountdown] = useState<number | null>(null)

  const advance = useCallback(() => {
    setAiSummary(null)
    setSummaryFailed(false)
    setEndReason(null)
    setElapsed(0)
    setHeld(false)
    // Decided outside the state updater: React can invoke an updater more than
    // once, so anything with a side effect cannot live inside one.
    if (index + 1 >= queue.length) {
      void engineRef.current?.endCall()
      setRunning(false)
      setStage("idle")
      message.success("Session complete")
      return
    }
    engineRef.current?.hangupCall()
    setIndex(index + 1)
    setDialNumberIndex(0)
    setStage("connecting")
  }, [queue.length, index])

  const logAndAdvance = useCallback(
    async (
      v: { disposition?: string; notes?: string; personId?: string },
      shouldAdvance: boolean
    ) => {
      const ok = await (engineRef.current?.log(v) ?? Promise.resolve(false))
      if (!ok) return
      // Counts PEOPLE logged, not log events. It incremented per call to this
      // function, so logging the same contact twice - a redial, or just
      // pressing Log call again - read as "3 / 1" on the session bar against a
      // one-person queue.
      const personId = v.personId ?? (current ? String(current.id) : "")
      setLogged((prev) => {
        if (!personId || prev.has(personId)) return prev
        const nextSet = new Set(prev)
        nextSet.add(personId)
        return nextSet
      })
      if (shouldAdvance) advance()
      else if (!next) {
        // The last contact has been logged and there is nothing to dial next.
        // Say the list is finished rather than leaving the rep looking at a
        // panel that has quietly stopped being useful.
        void engineRef.current?.endCall()
        setRunning(false)
        setStage("idle")
        message.success("List finished — that was the last contact.")
      }
    },
    [advance, current, next]
  )

  // Snooze SKIPS the contact and REMEMBERS it: hang up, mark the queue entry
  // SKIPPED, record the snooze, move on.
  //
  // It first moved them to the back of the queue, which meant a snoozed person
  // came round again inside the same session. Then it skipped without
  // recording, so the next session offered them again and nothing anywhere
  // knew the rep had decided. It persists now, with no expiry - the rep is
  // saying "stop putting this person in front of me", and only they undo it.
  const snooze = useCallback(() => {
    if (!current) return
    engineRef.current?.hangup()
    void engineRef.current?.markSkipped()
    // Fire and forget: the queue must move on at once, and the mutation
    // updates the snooze list optimistically so the row leaves the callable
    // side without waiting for the round trip.
    void setSnooze({ championId: String(current.id), snoozed: true }).catch(
      () => {
        // A failed snooze is worth telling the rep about - they will otherwise
        // see this person again and assume the button does nothing.
        message.warning(
          "Skipped, but could not save the snooze. They may come back in a later session."
        )
      }
    )
    setHeld(false)
    setElapsed(0)
    advance()
  }, [current, advance, setSnooze])

  // NOTHING rolls on by itself. The queue advances only when the rep says so.
  //
  // An unanswered call used to wait 2.5s and dial the next person. That beat
  // was measured against Twilio's 30 second ring, where it reads as a pause -
  // but the two cases that never ring cost only the gap itself: a person with
  // no number, skipped without dialling, and a number Twilio rejects outright.
  // A run of those advanced every 2.5 seconds and the queue visibly ran away
  // from the rep.
  //
  // Rather than tune the number, the auto-advance is gone. Every call now ends
  // on the wrap screen with the outcome in front of the rep, and "Log & dial
  // next" is the only thing that moves the queue - the same way a connected
  // call and a rep-ended call already behaved.

  // Just flips the flag. Nothing advances on its own any more, so coming off
  // pause leaves the rep exactly where they were.
  const togglePause = useCallback(() => setPaused((p) => !p), [])

  // Said once per press, outside the setter.
  // ENG-4380. Said "the current call continues" whether or not one was up, so
  // pausing with nothing dialling announced a call that did not exist. What
  // pause actually guarantees is that the NEXT person is not dialled, which is
  // true in both cases.
  useEffect(() => {
    if (running && paused) {
      message.info("Dialing paused. Next person will not be dialled")
    }
  }, [paused, running])

  // Auto-advance waits, visibly, instead of moving the moment a call ends.
  //
  // Advancing immediately reassigned the call the rep was still dispositioning,
  // so their outcome landed on the next person: in one session eleven of
  // twenty-seven calls disagreed with what Twilio recorded. The wait gives the
  // rep the call they just had for long enough to log it, and a way to stop the
  // run without ending the session.
  useEffect(() => {
    if (!running || paused || stage !== "wrap" || !autoAdvance) {
      setAdvanceCountdown(null)
      return
    }
    // Only a ring nobody picked up. Every other ending leaves the rep with
    // something to record: a call that connected has an outcome whoever hung
    // up first, and one they disconnected or skipped was already their
    // decision. Moving on for them would take the disposition off them, which
    // is the thing that went wrong when this advanced on its own.
    if (endReason !== "no_answer") {
      setAdvanceCountdown(null)
      return
    }
    if (dialNumberIndex + 1 < dialNumbers.length) {
      setAdvanceCountdown(null)
      return
    }
    setAdvanceCountdown(AUTO_ADVANCE_SECONDS)
  }, [
    running,
    paused,
    stage,
    autoAdvance,
    endReason,
    current?.id,
    dialNumberIndex,
    dialNumbers.length
  ])

  // A person snoozed while their call was connected. The row has already left
  // the callable side, but the conversation has not, so the run waits for it to
  // finish and then moves on by itself - the rep does not have to come back to
  // a call they have already dealt with.
  useEffect(() => {
    if (!running || paused || stage !== "wrap") return
    if (!snoozedDuringCallRef.current) return
    snoozedDuringCallRef.current = false
    advance()
  }, [running, paused, stage, advance])

  useEffect(() => {
    setDialNumberIndex(0)
  }, [current?.id])

  useEffect(() => {
    if (!running || paused || stage !== "wrap") return
    if (endReason !== "no_answer") return
    if (dialNumberIndex + 1 >= dialNumbers.length) return
    const nextNumber = dialNumbers[dialNumberIndex + 1]
    setDialNumberIndex(dialNumberIndex + 1)
    setEndReason(null)
    setStage("connecting")
    message.info(
      `No answer. Trying their ${nextNumber.label.toLowerCase()} number`
    )
  }, [running, paused, stage, endReason, dialNumberIndex, dialNumbers])

  useEffect(() => {
    if (stage === "connecting") snoozedDuringCallRef.current = false
  }, [stage, current?.id])

  const value: DialerRunValue = {
    dialNumbers,
    dialNumberIndex,
    activeDialNumber,
    calleeRinging,
    advanceCountdown,
    cancelAdvance: () => setAdvanceCountdown(null),
    advanceNow: () => {
      setAdvanceCountdown(null)
      advance()
    },
    snoozeCurrentAfterCall: () => {
      snoozedDuringCallRef.current = true
    },
    syncQueue,
    running,
    paused,
    stage,
    endReason,
    index,
    queue,
    current,
    next,
    elapsed,
    completed: logged.size,
    muted,
    held,
    deviceStatus,
    aiSummary,
    summaryFailed,
    start,
    end,
    togglePause,
    toggleMute: () => engineRef.current?.toggleMute(),
    sendDigits: (d) => engineRef.current?.sendDigits(d),
    toggleHold: async () => {
      const want = !held
      const ok = await (engineRef.current?.setHold(want) ??
        Promise.resolve(false))
      if (ok) setHeld(want)
    },
    // Dials the current person again rather than moving on. The engine places
    // a call whenever stage becomes "connecting", so re-entering that stage
    // without touching the index is the whole of it; the per-call state is
    // cleared first so the next leg does not inherit the last one's summary,
    // mute or hold.
    redial: () => {
      // Pausing means the queue stops after this call. Re-arming the current
      // person sets stage back to "connecting", which the dial effect ignores
      // while paused - and then fires the moment the rep resumes, redialling
      // someone they had already finished with instead of moving on.
      if (paused) return
      setAiSummary(null)
      setSummaryFailed(false)
      setEndReason(null)
      setElapsed(0)
      setHeld(false)
      setMuted(false)
      setStage("connecting")
    },
    snooze,
    disconnect: () => {
      setEndReason("manual")
      engineRef.current?.hangup()
      setStage("wrap")
    },
    logAndAdvance
  }

  return (
    <DialerRunContext.Provider value={value}>
      {running ? (
        <DialerEngine
          handleRef={engineRef}
          current={current}
          dialNumber={activeDialNumber}
          dialNumberIndex={dialNumberIndex}
          paused={paused}
          stage={stage}
          setStage={setStage}
          queue={queue}
          onMuted={setMuted}
          onDeviceStatus={setDeviceStatus}
          onSummary={setAiSummary}
          onSummaryFailed={() => setSummaryFailed(true)}
          onEndReason={setEndReason}
          onCalleeRinging={setCalleeRinging}
          onSessionId={(id) => (liveSessionRef.current = id)}
        />
      ) : null}
      {children}
    </DialerRunContext.Provider>
  )
}

// Owns the Twilio device for the duration of a run. Renders nothing.
function DialerEngine({
  handleRef,
  current,
  dialNumber,
  dialNumberIndex,
  paused,
  stage,
  setStage,
  queue,
  onMuted,
  onDeviceStatus,
  onSummary,
  onSummaryFailed,
  onEndReason,
  onSessionId,
  onCalleeRinging
}: {
  handleRef: React.MutableRefObject<EngineHandle | null>
  current: RunPerson | null
  paused: boolean
  stage: Stage
  setStage: (s: Stage) => void
  dialNumber: DialNumber | null
  dialNumberIndex: number
  queue: RunPerson[]
  onMuted: (m: boolean) => void
  onDeviceStatus: (s: string) => void
  onSummary: (s: string | null) => void
  onSummaryFailed: () => void
  onEndReason: (r: EndReason) => void
  onSessionId: (id: number | null) => void
  onCalleeRinging: (v: boolean) => void
}) {
  const {
    status: deviceStatus,
    makeCall,
    hangup,
    toggleMute,
    muted,
    logCall,
    ensureSession,
    enqueueQueue,
    reserveCall,
    markQueueEntryStatus,
    watchCallStream,
    sendDigits,
    endSession,
    notReadyRef
  } = useVoiceDevice()

  const tokenInfo = useGlobalStore(
    (store) => store.tokenInfo
  ) as ITokenInfo | null
  const callerConfig = useVoiceSessionStore((s) => s.callerConfig)
  const callerNumber = useVoiceSessionStore((s) => s.callerNumber)
  const callingHours = useVoiceSessionStore((s) => s.callingHours)

  const sessionIdRef = useRef<number | null>(null)
  // person id -> dialer_queue row id, from the enqueue response. reserveCall
  // and markQueueEntryStatus both address the QUEUE ENTRY, not the person, so
  // without this map there is no honest id to hand them.
  const queueIdsRef = useRef<Record<string, number>>({})
  const sessionReadyRef = useRef<Promise<void> | null>(null)
  const reservedCallIdRef = useRef<number | null>(null)
  const callSidRef = useRef<string | null>(null)
  const logTargetsRef = useRef<
    Record<string, { sid: string | null; callId: number | null }>
  >({})
  const rememberLogTarget = (
    personId: string | undefined,
    patch: { sid?: string | null; callId?: number | null }
  ) => {
    if (!personId) return
    const key = String(personId)
    const prev = logTargetsRef.current[key] ?? { sid: null, callId: null }
    logTargetsRef.current[key] = { ...prev, ...patch }
  }
  const stopWatchingRef = useRef<(() => void) | null>(null)
  const watchedSidRef = useRef<string | null>(null)
  const dialTokenRef = useRef(0)
  // Why the last call ended, which decides whether the run rolls on by itself.
  const endReasonRef = useRef<EndReason>(null)
  // Set by the rep pressing Disconnect. finish() runs for every ending, and it
  // reported the answered/no-answer shape of the call, which overwrote the one
  // fact that says the rep was the one who ended it.
  const manualHangupRef = useRef(false)

  useEffect(() => onMuted(muted), [muted, onMuted])

  // THE SOURCE OF TRUTH FOR "IS THIS CALL ANSWERED" IS THE SERVER.
  //
  // call.parameters.CallSid is empty on an outbound Twilio Client call — even
  // inside the "accept" handler — so the SSE status stream could never be
  // opened (it is keyed by sid) and the panel sat on "Ringing" through an
  // 18-second conversation. Twilio's answer webhook lands on dialer-service
  // and updates dialer_calls, so that row is what actually knows.
  //
  // Polled while a call is in flight: it yields the sid (which Hold needs),
  // the ANSWERED transition (which starts the timer and triggers talking
  // points), and the terminal status.
  useEffect(() => {
    // Keeps polling through "wrap": the AI summary is written after the call
    // completes, so stopping the moment it ends would never see it.
    if (
      !current ||
      (stage !== "ringing" && stage !== "live" && stage !== "wrap")
    )
      return
    if (!tokenInfo) return
    let cancelled = false

    const poll = async () => {
      try {
        const res = await getDialerCallHistory(tokenInfo, {
          tab: "all",
          personIds: [current.id]
        })
        if (cancelled) return
        const calls = res?.data?.data?.calls ?? []
        // The row for THIS attempt: newest, and created since we dialled.
        const reservedId = reservedCallIdRef.current
        const attemptSid = callSidRef.current
        const row =
          reservedId != null
            ? calls.find((c) => Number(c.id) === Number(reservedId))
            : attemptSid
              ? calls.find((c) => c.twilio_call_sid === attemptSid)
              : undefined
        if (!row) return
        if (row.twilio_call_sid) {
          callSidRef.current = row.twilio_call_sid
          rememberLogTarget(current?.id, { sid: row.twilio_call_sid })
        }

        // The AI summary comes from its OWN endpoint, which reports
        // pending/ready/failed. Reading it off the list row does not work: the
        // list does not return the column, which is why the panel sat on
        // "Writing the summary…" for a call whose summary was already written.
        if (row.status === "COMPLETED" || row.twilio_call_sid) {
          try {
            const sum = await getDialerCallSummary(
              tokenInfo,
              current.id,
              row.twilio_call_sid
            )
            const text = sum?.data?.summary
            if (text) onSummary(text)
            else if (sum?.data?.status === "failed") onSummaryFailed()
          } catch {
            // Still generating, or not reachable yet — the next tick retries.
          }
        }
        // The summary fetch above is a second await, and the run can move
        // during it. Without re-checking, a poll that began on the previous
        // call resumes after a redial has started and reports that call's
        // terminal status against the one now ringing - which is ENG-4375
        // arriving on a slow summary response and not at all on a fast one.
        if (cancelled) return
        if (
          callSidRef.current &&
          row.twilio_call_sid &&
          row.twilio_call_sid !== callSidRef.current
        ) {
          return
        }
        if (row.status === "RINGING") onCalleeRinging(true)
        if (row.status === "ANSWERED") {
          endReasonRef.current = "answered"
          setStage("live")
        } else if (TERMINAL_CALL_STATUSES.includes(row.status)) {
          if (endReasonRef.current === null) {
            endReasonRef.current =
              row.status === "COMPLETED" && (row.duration_seconds ?? 0) > 0
                ? "answered"
                : "no_answer"
          }
          onEndReason(endReasonRef.current)
          setStage("wrap")
        }
      } catch {
        // A poll that fails is retried by the next tick.
      }
    }

    void poll()
    const t = setInterval(poll, 2000)
    return () => {
      cancelled = true
      clearInterval(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, stage, tokenInfo])
  useEffect(() => onDeviceStatus(deviceStatus), [deviceStatus, onDeviceStatus])

  // Open the session and queue the run once, when the engine mounts.
  useEffect(() => {
    let cancelled = false
    sessionReadyRef.current = (async () => {
      try {
        const sessionId = await ensureSession(
          completeCallingHours(callingHours),
          true
        )
        if (cancelled) return
        sessionIdRef.current = sessionId ?? null
        onSessionId(sessionId ?? null)
        const dialable = queue.filter((p) => p.phone && /\d/.test(p.phone))
        if (sessionId && dialable.length) {
          const entries = (await enqueueQueue(
            sessionId,
            dialable.map((p) => p.id)
          )) as IDialerQueueEntry[] | null
          if (cancelled) return
          const map: Record<string, number> = {}
          for (const e of entries ?? []) {
            const pid = entryPersonId(e)
            if (pid) map[pid] = e.id
          }
          queueIdsRef.current = map
        }
      } catch {
        message.error("Could not open a calling session")
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Place the call for whoever is current. Guarded on stage so a re-render
  // cannot dial the same person twice.
  //
  // WAITS FOR THE DEVICE. The engine mounts at the same moment the run starts,
  // and building plus registering the Twilio device is asynchronous — dialing
  // straight away raced it and makeCall returned null, which surfaced as "the
  // dialer is not connected yet" on the very first call of every session. The
  // effect re-runs when the status changes, so the queue starts the instant the
  // device is ready. The old modal gated on the same `ready` status.
  useEffect(() => {
    if (paused || stage !== "connecting" || !current) return
    if (deviceStatus === "error") {
      message.error("The dialer could not connect. Reload and try again.")
      return
    }
    if (deviceStatus !== "ready") return
    const token = ++dialTokenRef.current
    const stillMine = () => dialTokenRef.current === token
    setStage("ringing")
    ;(async () => {
      // ENG-4375. The status poll finds "the row for this attempt" by the
      // reserved id, falling back to callSidRef. reservedCallIdRef was cleared
      // here and callSidRef was NOT, so a redial re-armed with the PREVIOUS
      // call's sid still set: the poll matched that completed row and pushed
      // stage straight back to "wrap" while the new call was ringing. The
      // device's own accept event then pushed it to "live" again, and the two
      // fought every two seconds - Disconnect flicking red to grey, Redial
      // live during a live call.
      //
      // The old watcher goes with it. It is keyed to the finished call's sid
      // and has nothing left to report.
      endReasonRef.current = null
      manualHangupRef.current = false
      onCalleeRinging(false)
      reservedCallIdRef.current = null
      callSidRef.current = null
      stopWatchingRef.current?.()
      stopWatchingRef.current = null
      watchedSidRef.current = null
      onEndReason(null)
      const to = dialNumber?.phone || current.phone || ""
      if (!to) {
        // No number is a person to skip, not a failure to report. Reported as
        // its own reason rather than folded into no_answer: nothing rang, so
        // the only thing marking that this person went past is the pause
        // before the next one, and that pause is longer.
        endReasonRef.current = "skipped"
        onEndReason("skipped")
        setStage("wrap")
        return
      }

      const params: Record<string, string> = {}
      if (callerConfig.countryMatch && current.location_country) {
        params.calleeCountry = current.location_country
      }
      if (callerConfig.rotation && callerConfig.rotateN) {
        params.rotateAfter = String(callerConfig.rotateN)
      }
      // Also sent directly to voiceAnswer, not just reserveCall — if the
      // reserve step below fails or has no queue entry to attach to,
      // voiceAnswer falls back to inserting the dialer_calls row itself and
      // needs list_id from here to still record it.
      if (current.listId) {
        params.list_id = current.listId
      }

      // Reserve the dialer_calls row before Twilio is invoked, so the log has
      // something to attach to even if the call never connects. Only possible
      // for someone who made it into the queue — reserving against an invented
      // id would be worse than not reserving at all.
      if (sessionReadyRef.current) {
        try {
          await sessionReadyRef.current
        } catch {
          void 0
        }
      }
      if (!stillMine()) return

      const queueId = queueIdsRef.current[current.id]
      if (sessionIdRef.current && queueId) {
        try {
          const reserved = await reserveCall(
            sessionIdRef.current,
            queueId,
            to,
            callerNumber || undefined,
            current.linkedin_url,
            current.listId
          )
          reservedCallIdRef.current = reserved ?? null
          rememberLogTarget(current?.id, { callId: reserved ?? null })
          // Links this leg to the row just reserved. voiceAnswer updates by
          // queue_id; without it every call inserted a SECOND dialer_calls
          // row (a QUEUED one from the reserve, plus the real one), so the
          // reserved row was orphaned and the queue entry never matched the
          // call that actually happened.
          if (reserved) params.queue_id = String(queueId)
        } catch {
          // Reserving is bookkeeping; a failure must not block the call.
        }
      }

      const call = await makeCall(
        to,
        params,
        current.id,
        completeCallingHours(callingHours),
        current.linkedin_url
      )
      if (!stillMine()) return
      if (!call) {
        // The device is ready by this point, so a null call is a real
        // failure: microphone refused, or a token that would not mint.
        const reason = notReadyRef?.current
        message.error(
          reason === "mic"
            ? "Allow microphone access to place calls"
            : reason === "token"
              ? "Could not authenticate the call. Reload and try again."
              : "Could not place that call"
        )
        setStage("wrap")
        return
      }
      const c = call as unknown as {
        on: (e: string, fn: () => void) => void
        disconnect?: () => void
        parameters?: { CallSid?: string }
      }

      // Disconnect pressed while this call was still being placed.
      //
      // makeCall is awaited, so between "ringing" appearing and the Call object
      // existing there is nothing for hangup() to act on: it no-opped, the
      // panel went to Call ended because disconnect sets that itself, and then
      // makeCall resolved and rang the prospect anyway. The rep watched a call
      // they had already cancelled connect.
      if (manualHangupRef.current) {
        try {
          c.disconnect?.()
        } catch {
          // Already gone; hangup below is the backstop.
        }
        hangup()
        return
      }

      // The sid arrives WITH "accept", not before it.
      //
      // On an outbound call call.parameters carries the params we sent; Twilio
      // fills CallSid in when the leg is established. Reading it any earlier
      // returned undefined, so the status stream was never opened — no
      // ANSWERED, no timer, and Hold had no leg to act on.
      const beginWatching = () => {
        if (!stillMine()) return
        const sid = c.parameters?.CallSid ?? null
        callSidRef.current = sid
        rememberLogTarget(current?.id, { sid })
        if (!sid || watchedSidRef.current === sid) return
        stopWatchingRef.current?.()
        watchedSidRef.current = sid
        // The REAL pickup comes from the backend: Twilio's answer webhook
        // lands server-side and the status turns ANSWERED. That is what makes
        // the timer measure talk time rather than ring time, and it is what
        // triggers talking-point generation on the server.
        stopWatchingRef.current = watchCallStream(sid, {
          onStatus: (status) => {
            if (status === "ANSWERED") {
              endReasonRef.current = "answered"
              setStage("live")
            } else if (TERMINAL_CALL_STATUSES.includes(status)) {
              // Nobody picked up. That is the case that rolls on by itself.
              if (endReasonRef.current === null) {
                endReasonRef.current =
                  status === "COMPLETED" ? "answered" : "no_answer"
              }
              onEndReason(endReasonRef.current)
              setStage("wrap")
            }
          },
          onSummary,
          onFailed: () => {}
        })
      }

      c.on("accept", beginWatching)
      // Belt and braces: if accept already fired before these handlers were
      // attached, the sid may be sitting there now.
      beginWatching()

      const finish = () => {
        // These are bound to THIS leg's Call object, which outlives the leg.
        // A redial places a new call while the old one is still tearing down,
        // and its disconnect then ran against the attempt that replaced it:
        // the panel showed Call ended through the whole ring, and only came
        // back when the callee answered and accept forced it live again.
        if (!stillMine()) return
        stopWatchingRef.current?.()
        stopWatchingRef.current = null
        watchedSidRef.current = null
        if (manualHangupRef.current) endReasonRef.current = "manual"
        else if (endReasonRef.current === null)
          endReasonRef.current = "no_answer"
        onEndReason(endReasonRef.current)
        setStage("wrap")
      }
      c.on("disconnect", finish)
      c.on("cancel", finish)
      c.on("reject", finish)
      c.on("error", finish)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, stage, current?.id, deviceStatus, dialNumberIndex])

  useEffect(
    () => () => {
      stopWatchingRef.current?.()
      stopWatchingRef.current = null
      watchedSidRef.current = null
    },
    []
  )

  // Publish the device-backed actions upward.
  useEffect(() => {
    handleRef.current = {
      hangup: () => {
        manualHangupRef.current = true
        hangup()
      },
      // Tears down a run's call on BOTH sides, as far as this app can reach:
      //
      //   1. the browser leg — Call.disconnect() on the live Twilio Call, plus
      //      the status stream that was following it.
      //   2. the server-side SESSION — dialer_sessions marked complete, so a
      //      run the rep walked away from does not stay open and the next
      //      ensureSession does not resume it.
      //
      // WHAT THIS CANNOT DO: there is no call-level hangup on dialer-service to
      // call from here. /voice/calls/:sid exposes hold, voicemail and decline
      // only (decline is for an inbound ring, not an outbound leg), and
      // completing the session does not touch a leg that is still up. Twilio
      // normally drops the PSTN leg when the client leg it was dialled for
      // disconnects, and that is the only thing stopping the callee's phone
      // today. If it ever fails to propagate, nothing here can end that leg —
      // dialer-service needs to expose a hangup for the sid.
      endCall: async () => {
        hangup()
        stopWatchingRef.current?.()
        stopWatchingRef.current = null
        watchedSidRef.current = null
        await endSession()
      },
      hangupCall: () => {
        hangup()
        stopWatchingRef.current?.()
        stopWatchingRef.current = null
        watchedSidRef.current = null
      },
      toggleMute,
      sendDigits,
      // Hold is a server action on the live leg: dialer-service moves the
      // caller to hold music. Without a call sid there is no leg to hold, so
      // it reports that instead of flipping a button that does nothing.
      setHold: async (hold) => {
        if (!tokenInfo || !callSidRef.current) {
          message.info("Nothing to hold yet")
          return false
        }
        try {
          await setCallHold(tokenInfo, callSidRef.current, hold)
          return true
        } catch {
          message.error(hold ? "Could not hold the call" : "Could not resume")
          return false
        }
      },
      // Snooze marks the queue entry SKIPPED so the session record shows the
      // rep passed on them deliberately rather than the call simply not
      // happening.
      markSkipped: async () => {
        const queueId = current ? queueIdsRef.current[current.id] : undefined
        if (!sessionIdRef.current || !queueId) return
        await markQueueEntryStatus(
          sessionIdRef.current,
          queueId,
          "SKIPPED"
        ).catch(() => {})
      },
      log: async ({ disposition, notes, personId }) => {
        try {
          const target = personId
            ? logTargetsRef.current[String(personId)]
            : undefined
          const sid = target ? target.sid : callSidRef.current
          const callId = target ? target.callId : reservedCallIdRef.current
          await logCall(sid, {
            disposition,
            notes,
            fallbackCallId: callId ?? undefined
          })
          const logKey = personId ? String(personId) : null
          const queueId = logKey
            ? queueIdsRef.current[logKey]
            : current
              ? queueIdsRef.current[current.id]
              : undefined
          if (sessionIdRef.current && queueId) {
            await markQueueEntryStatus(
              sessionIdRef.current,
              queueId,
              "CALLED"
            ).catch(() => {})
          }
          if (logKey) delete logTargetsRef.current[logKey]
          // The live refs follow the call in flight, not the one just written,
          // so they are cleared whenever the log was for the person the run is
          // on. Gating this on whether a per-person target was used left the
          // finished call's sid in place for every normal log, which is the
          // state ENG-4375 is about: the poll then matches that completed row
          // on the next attempt. They are left alone only when the log belongs
          // to somebody the run has already moved past.
          if (!logKey || (current && String(current.id) === logKey)) {
            callSidRef.current = null
            reservedCallIdRef.current = null
          }
          return true
        } catch {
          message.error("Could not save the call log")
          return false
        }
      }
    }
    return () => {
      handleRef.current = null
    }
  }, [
    handleRef,
    hangup,
    toggleMute,
    logCall,
    markQueueEntryStatus,
    endSession,
    current,
    tokenInfo
  ])

  return null
}
